import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingCart, Leaf, Clock, Utensils, Box, Minus, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";
import { LoginPromptDialog } from "@/components/LoginPromptDialog";
import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addedQty, setAddedQty] = useState(0);
  const [subscription, setSubscription] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
        
        // Fetch related products
        const relatedRes = await axios.get(`${API}/products?category=${response.data.category}`);
        setRelatedProducts(relatedRes.data.filter(p => p.slug !== slug).slice(0, 4));
      } catch (error) {
        console.error("Failed to fetch product:", error);
        toast.error("Product not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const handleAddToCart = async () => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      await addToCart(product.id, 1, subscription, subscription ? frequency : null);
      setAddedQty(1);
      toast.success(`Added ${product.name} to cart!`);
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const handleQtyChange = async (delta) => {
    const newQty = addedQty + delta;
    if (newQty <= 0) {
      setAddedQty(0);
      toast.info("Removed from cart");
      return;
    }
    try {
      await addToCart(product.id, delta);
      setAddedQty(newQty);
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 lg:pt-24 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted animate-pulse rounded-2xl" />
            <div className="space-y-4">
              <div className="h-8 w-32 bg-muted animate-pulse rounded" />
              <div className="h-12 w-3/4 bg-muted animate-pulse rounded" />
              <div className="h-24 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-16 md:pt-20 lg:pt-24 min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-praroha-forest mb-4">Product Not Found</h1>
          <Link to="/products">
            <Button variant="outline">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const price = subscription
    ? (product.price * 0.9).toFixed(0)
    : product.price;

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="product-detail-page">
      {product && (
        <>
          <SEO
            title={product.name}
            description={product.short_description}
            keywords={`${product.name}, microgreens, ${product.category}, buy microgreens`}
            path={`/products/${product.slug}`}
            image={product.image}
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                "name": product.name,
                "description": product.description,
                "image": product.image,
                "brand": { "@type": "Brand", "name": "Praroha Greens" },
                "offers": {
                  "@type": "Offer",
                  "price": product.price,
                  "priceCurrency": "INR",
                  "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                  "seller": { "@type": "Organization", "name": "Praroha Greens" }
                },
                "category": product.category,
                "weight": { "@type": "QuantitativeValue", "value": product.weight }
              })}
            </script>
          </Helmet>
        </>
      )}
      {/* Breadcrumb */}
      <div className="bg-praroha-cream/50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-praroha-forest transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Link>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:sticky lg:top-32 lg:h-fit"
            >
              <div className="aspect-square overflow-hidden rounded-2xl bg-praroha-cream/50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              </div>
              {product.gallery && product.gallery.length > 0 && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {product.gallery.map((img, i) => (
                    <div key={i} className="aspect-square overflow-hidden rounded-lg bg-praroha-cream/50">
                      <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-block px-3 py-1 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full capitalize mb-4">
                {product.category}
              </span>
              
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-praroha-forest mb-4" data-testid="product-name">
                {product.name}
              </h1>
              
              <p className="text-muted-foreground text-lg mb-6">
                {product.description}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-heading text-3xl font-bold text-praroha-forest" data-testid="product-price">
                  ₹{price}
                </span>
                {subscription && (
                  <span className="text-sm text-praroha-terracotta font-medium">10% subscription discount</span>
                )}
              </div>

              {/* Add to Cart */}
              <div className="mb-8" data-testid="add-to-cart-section">
                {!product.in_stock ? (
                  <Button size="lg" disabled className="w-full rounded-full py-6 text-lg">
                    Out of Stock
                  </Button>
                ) : addedQty > 0 ? (
                  <div className="flex items-center gap-4">
                    <div className="inline-flex items-center bg-praroha-forest rounded-full shadow-lg shadow-praroha-forest/25">
                      <button
                        className="w-14 h-14 flex items-center justify-center text-white/80 hover:text-white active:scale-90 transition-all"
                        onClick={() => handleQtyChange(-1)}
                        data-testid="quantity-decrease"
                      >
                        <Minus className="w-5 h-5" strokeWidth={2.5} />
                      </button>
                      <span className="w-10 text-center text-xl font-bold text-white tabular-nums" data-testid="quantity-value">
                        {addedQty}
                      </span>
                      <button
                        className="w-14 h-14 flex items-center justify-center text-white/80 hover:text-white active:scale-90 transition-all"
                        onClick={() => handleQtyChange(1)}
                        data-testid="quantity-increase"
                      >
                        <Plus className="w-5 h-5" strokeWidth={2.5} />
                      </button>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-praroha-forest">In your cart</p>
                      <p className="text-xs text-muted-foreground">₹{(product.price * addedQty).toFixed(0)} total</p>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="lg"
                    className="w-full bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full py-6 text-lg"
                    onClick={handleAddToCart}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                )}
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 p-3 bg-praroha-cream/50 rounded-xl">
                  <Leaf className="w-5 h-5 text-praroha-sage" />
                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="text-sm font-medium text-praroha-forest capitalize">{product.category}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-praroha-cream/50 rounded-xl">
                  <Clock className="w-5 h-5 text-praroha-sage" />
                  <div>
                    <p className="text-xs text-muted-foreground">Freshness</p>
                    <p className="text-sm font-medium text-praroha-forest">7 days</p>
                  </div>
                </div>
              </div>

              {/* Accordion Details */}
              <Accordion type="single" collapsible className="w-full" data-testid="product-accordion">
                <AccordionItem value="nutrition">
                  <AccordionTrigger className="text-praroha-forest hover:text-praroha-sage">
                    <span className="flex items-center gap-2">
                      <Leaf className="w-4 h-4" />
                      Nutritional Benefits
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2">
                      {product.nutritional_benefits.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-2 text-muted-foreground">
                          <Check className="w-4 h-4 text-praroha-sage" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="flavor">
                  <AccordionTrigger className="text-praroha-forest hover:text-praroha-sage">
                    <span className="flex items-center gap-2">
                      <Utensils className="w-4 h-4" />
                      Flavor Profile & Best Dishes
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-muted-foreground mb-4">{product.flavor_profile}</p>
                    <p className="font-medium text-praroha-forest mb-2">Best paired with:</p>
                    <ul className="space-y-1">
                      {product.best_dishes.map((dish, i) => (
                        <li key={i} className="text-muted-foreground">• {dish}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-background to-praroha-cream/30" data-testid="related-products">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-praroha-forest mb-8">
              You May Also Like
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((product) => (
                <Link key={product.id} to={`/products/${product.slug}`}>
                  <Card className="group h-full bg-white overflow-hidden border-border/40 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-500">
                    <div className="aspect-square overflow-hidden bg-praroha-cream/50">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-heading text-lg font-semibold text-praroha-forest group-hover:text-praroha-sage transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">₹{product.price}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <LoginPromptDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt} />
    </div>
  );
}
